/*
 * Quantity drives the MAIN price number.
 *
 * Nick, 10 Aug: "the change of pricing is supposed to adjust the main number. not
 * add a new number below it. the sizes change makes the main number move up and
 * down so the how many pairs you wanna buy should also adjust the same number."
 *
 * BEFORE: this script appended a second line under the quantity stepper reading
 * "2 x $156.00 NZD = $312.00 NZD", leaving the big price showing the unit price.
 * Two prices on screen, and the one the eye lands on was not the one being
 * charged.
 * AFTER: the big price itself shows the line total. Picking a size moves it
 * (Dawn already does that) and changing the quantity moves the same number.
 *
 * How it stays correct:
 *
 *  - The unit price is captured once per price element into data-eh-unit, and
 *    every later render multiplies from THAT, never from whatever is currently
 *    displayed. Reading the displayed value back would compound on itself the
 *    moment the observer fired on our own write.
 *  - Only the numeric run inside the element is swapped, so the currency symbol,
 *    the "NZD" suffix and any surrounding markup survive untouched rather than
 *    being rebuilt from assumptions about NZ formatting.
 *  - Dawn replaces the whole .price node on a variant change, which drops our
 *    data attribute with it. That is the desired behaviour: the fresh node is
 *    re-captured at its new unit price and re-multiplied. It also means .price
 *    must be looked up fresh on EVERY render -- an earlier version captured it
 *    once at init, and after any variant change it was holding a detached node,
 *    so the stepper silently stopped moving the price until a page reload.
 *  - The observer therefore watches the stable info container, not .price.
 *  - Writes are idempotent (nothing is written when the text already matches),
 *    so an observer callback triggered by our own write terminates instead of
 *    looping. The re-entrancy flag skips the common case cheaply.
 *
 * NOTE: Dawn associates the quantity input with the add-to-cart form via the
 * input's form="..." attribute, not by nesting it inside <form>. Scope from the
 * quantity input's own container -- an earlier version searched
 * `form[action*="/cart/add"] quantity-input` and silently found nothing on every
 * product page.
 */
(function () {
  var writing = false;

  var PRICE_SEL = '.price-item--regular, .price-item--sale, .price__regular .price-item, .price__sale .price-item';

  function parseAmount(text) {
    // Keep the LAST run of digits/separators, so "Sale price $156.00 NZD" and
    // "$1,299.00" both resolve, and a stray leading number cannot win.
    var m = String(text).match(/[\d][\d.,\s]*/g);
    if (!m || !m.length) return null;
    // Trailing whitespace belongs to the TEMPLATE, not to the number. The class
    // above has to allow \s for space-grouped thousands, but that also swallowed
    // the space before the currency code: raw came back as "98.00 ", the rewrite
    // put back "98.00", and every product price rendered as "$98.00NZD".
    var last = m[m.length - 1].replace(/\s+$/, '');
    var raw = last.replace(/\s/g, '');
    // en-NZ: comma groups thousands, dot is the decimal point.
    var num = parseFloat(raw.replace(/,/g, ''));
    return isNaN(num) ? null : { value: num, raw: last };
  }

  function formatLike(sample, value) {
    var hasComma = /\d,\d{3}/.test(sample);
    var decimals = (sample.split('.')[1] || '').replace(/\D/g, '').length;
    if (decimals !== 0 && decimals !== 2) decimals = 2;
    var out = value.toFixed(decimals);
    if (hasComma || value >= 1000) {
      var parts = out.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      out = parts.join('.');
    }
    return out;
  }

  function applyTo(el, qty) {
    var stored = el.getAttribute('data-eh-unit');
    if (stored === null) {
      var parsed = parseAmount(el.textContent);
      if (!parsed) return;
      stored = String(parsed.value);
      el.setAttribute('data-eh-unit', stored);
      el.setAttribute('data-eh-tpl', el.textContent);
    }
    var unit = parseFloat(stored);
    if (isNaN(unit)) return;

    var tpl = el.getAttribute('data-eh-tpl') || el.textContent;
    var sample = parseAmount(tpl);
    if (!sample) return;

    var next = formatLike(sample.raw, unit * (qty > 0 ? qty : 1));
    var idx = tpl.lastIndexOf(sample.raw);
    var text = tpl.slice(0, idx) + next + tpl.slice(idx + sample.raw.length);
    if (el.textContent !== text) el.textContent = text;
  }

  function initOne(qtyInput) {
    if (qtyInput.dataset.ehQtyPriceInit) return;
    var scope =
      qtyInput.closest('.product__info-container, .product__info-wrapper, .product, .shopify-section') ||
      document;
    if (!(scope.querySelector('.price') || document.querySelector('.price'))) return;
    qtyInput.dataset.ehQtyPriceInit = '1';

    function render() {
      if (writing) return;
      writing = true;
      try {
        var priceRoot = scope.querySelector('.price') || document.querySelector('.price');
        if (!priceRoot) return;
        var qty = parseInt(qtyInput.value, 10);
        if (!qty || qty < 1) qty = 1;
        priceRoot.querySelectorAll(PRICE_SEL).forEach(function (el) {
          applyTo(el, qty);
        });
      } finally {
        writing = false;
      }
    }

    qtyInput.addEventListener('change', render);
    qtyInput.addEventListener('input', render);

    var anchor = qtyInput.closest('.product-form__input--quantity') ||
                 qtyInput.closest('quantity-input') || qtyInput;
    anchor.addEventListener('click', function (e) {
      if (e.target.closest('button[name="plus"], button[name="minus"], .quantity__button')) {
        setTimeout(render, 0);
      }
    });

    // Dawn swaps the whole .price node on a variant change, so observe the
    // container that survives the swap rather than the node that does not.
    new MutationObserver(function () {
      if (writing) return;
      setTimeout(render, 0);
    }).observe(scope === document ? document.body : scope,
               { childList: true, subtree: true, characterData: true });

    render();
  }

  function initAll() {
    document.querySelectorAll('quantity-input input, input[name="quantity"]').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
